import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMustache from "react-mustache";
import QRCode from "qrcode.react";
import { isMobileApp } from "../../../actions/Util";
import { DEFAULT_FIELDS } from "./printFields";

export const BASE_ROUTE = "/home/settings/printtemplates";

// The store being set up travels in the URL, so back and forward keep it
export const storeLocation = (storeKey, path = "", state) => ({
  pathname: BASE_ROUTE + path,
  search: `?store=${storeKey}`,
  state
});

// CSS px of the printed page. Thermal rolls have no fixed height.
const PAGES = {
  A4: { width: 794, height: 1123 },
  A5: { width: 559, height: 794 },
  "3 inch": { width: 300 },
  "2 inch": { width: 250 }
};

export const getPage = (size) => PAGES[size] || PAGES.A4;

export const SIZES = ["A4", "A5", "3 inch", "2 inch"];

// [{ size, items }] in paper order. A5 and 3 inch always appear so a size with
// nothing yet can still be added; 2 inch and unknown sizes only when in use.
export const groupBySize = (items, sizeOf) => {
  const extra = items.map(sizeOf).filter((s) => !SIZES.includes(s));
  return [...SIZES, ...new Set(extra)]
    .map((size) => ({ size, items: items.filter((i) => sizeOf(i) === size) }))
    .filter((g) => g.items.length > 0 || g.size !== "2 inch");
};

// Tallest the main preview may be: the web has room for the whole page
export const getPreviewHeight = () =>
  isMobileApp() ? window.innerHeight * 0.5 : window.innerHeight - 268;

export const getThumbHeight = () => (isMobileApp() ? 96 : 128);

// Designs are the built-in PrintTemplates records restyled with CSS, so each one
// keeps every field and the store's show/hide settings
const MODERN_CSS = `
  body { font-family: Arial, sans-serif; color: #1f2937; }
  table, th, td { border: 0; }
  th { padding: 6px 4px; background: #1f2937; color: #fff;
       -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  td { padding: 5px 4px; border-bottom: 1px solid #e5e7eb; }
  .table1 td { border-bottom: 0; }
  hr { border: 0; border-top: 2px solid #1f2937; }`;

const MINIMAL_CSS = `
  body { font-family: Arial, sans-serif; color: #111827; }
  table, th, td { border: 0; }
  th { padding: 6px 4px; border-bottom: 1.5px solid #111827; text-align: left; }
  td { padding: 4px; }
  hr { border: 0; border-top: 1px solid #d1d5db; }`;

const COMPACT_CSS = `
  body { font-family: Arial, sans-serif; font-size: 11px; }
  table, th, td { border: 0; }
  th { border-bottom: 1px dashed #000; }
  hr { border: 0; border-top: 1px dashed #000; }`;

export const GALLERY = [
  { id: "a4-classic", name: "Classic", size: "A4", source: "A4_default" },
  { id: "a4-modern", name: "Modern", size: "A4", source: "A4_default", css: MODERN_CSS },
  { id: "a4-minimal", name: "Minimal", size: "A4", source: "A4_default", css: MINIMAL_CSS },
  { id: "a5-classic", name: "Classic", size: "A5", source: "A5_default" },
  { id: "a5-modern", name: "Modern", size: "A5", source: "A5_default", css: MODERN_CSS },
  { id: "a5-minimal", name: "Minimal", size: "A5", source: "A5_default", css: MINIMAL_CSS },
  { id: "3in-classic", name: "Classic", size: "3 inch", source: "3 inch_default" },
  { id: "3in-compact", name: "Compact", size: "3 inch", source: "3 inch_default", css: COMPACT_CSS }
];

export const applyDesignCss = (template, css) => {
  if (!css) return template;
  const style = `<style>${css}</style>`;
  return template.includes("</head>")
    ? template.replace("</head>", style + "</head>")
    : style + template;
};

/* ---------- Bank details and signature ---------- */

// Written into the template itself, so they print everywhere the template is
// used. The start comment keeps the values for editing them later.
const EXTRAS_BLOCK = /<!--px-extras-start[\s\S]*?<!--px-extras-end-->/;
const EXTRAS_DATA = /<!--px-extras-start ([^>]*?)-->/;
const SIGNATURE_IMAGE = /<img data-px-signature src="([^"]*)"/;
const QR_IMAGE = /<img data-px-qr src="([^"]*)"/;
const NO_EXTRAS = { bank: null, signature: null, qr: null };

const escapeHtml = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\{/g, "&#123;")
    .replace(/\}/g, "&#125;");

// "--" would end the HTML comment
const encodeExtras = (data) =>
  encodeURIComponent(JSON.stringify(data)).replace(/-/g, "%2D");

export const BANK_FIELDS = [
  { key: "AccountName", label: "Account Name" },
  { key: "BankName", label: "Bank Name" },
  { key: "AccountNo", label: "Account No" },
  { key: "IFSC", label: "IFSC" },
  { key: "Branch", label: "Branch" },
  { key: "UPI", label: "UPI ID" }
];

const bankHtml = (bank) => {
  const rows = BANK_FIELDS.filter((f) => bank[f.key]).map(
    (f) => `<div>${f.label}: ${escapeHtml(bank[f.key])}</div>`
  );
  return `<div style="font-weight:bold;margin-bottom:4px">Bank Details</div>${rows.join("")}`;
};

const signatureHtml = (signature) =>
  (signature.image
    ? `<img data-px-signature src="${signature.image}" style="max-width:160px;max-height:64px" /><br />`
    : "") +
  (signature.name ? `<div>${escapeHtml(signature.name)}</div>` : "") +
  `<div style="font-weight:bold">${escapeHtml(signature.label)}</div>`;

const upiLink = ({ upi, name }) =>
  `upi://pay?pa=${encodeURIComponent(upi)}&pn=${encodeURIComponent(name || "")}&cu=INR`;

const qrHtml = (qr) =>
  (qr.mode === "image"
    ? `<img data-px-qr src="${qr.image}" style="width:96px;height:96px" />`
    : renderToStaticMarkup(<QRCode value={upiLink(qr)} size={96} renderAs="svg" />)) +
  (qr.label ? `<div style="font-weight:bold">${escapeHtml(qr.label)}</div>` : "");

export const readExtras = (template) => {
  const match = (template || "").match(EXTRAS_DATA);
  if (!match) return NO_EXTRAS;
  const data = { ...NO_EXTRAS, ...JSON.parse(decodeURIComponent(match[1])) };
  if (data.signature) {
    const image = template.match(SIGNATURE_IMAGE);
    data.signature.image = image ? image[1] : "";
  }
  if (data.qr && data.qr.mode === "image") {
    const image = template.match(QR_IMAGE);
    data.qr.image = image ? image[1] : "";
  }
  return data;
};

export const removeExtras = (template) => (template || "").replace(EXTRAS_BLOCK, "");

export const writeExtras = (template, { bank, signature, qr }, size) => {
  const clean = removeExtras(template);
  if (!bank && !signature && !qr) return clean;

  const stored = {
    bank,
    signature: signature && { label: signature.label, name: signature.name },
    qr: qr && { label: qr.label, mode: qr.mode, upi: qr.upi, name: qr.name }
  };
  const parts = [
    bank && { html: bankHtml(bank), align: "left" },
    qr && { html: qrHtml(qr), align: "center" },
    signature && { html: signatureHtml(signature), align: "right" }
  ].filter(Boolean);

  // A thermal roll is too narrow for columns
  const stacked = !getPage(size).height;
  const cell = (part) =>
    `<td style="border:0;padding:4px 0;vertical-align:top;font-size:12px;` +
    `text-align:${stacked ? "center" : part.align}">${part.html}</td>`;
  const rows = stacked
    ? parts.map((part) => `<tr>${cell(part)}</tr>`).join("")
    : `<tr>${parts.map(cell).join("")}</tr>`;
  const block =
    `<!--px-extras-start ${encodeExtras(stored)}-->` +
    `<table style="width:100%;border:0;margin:8px 0">${rows}</table><!--px-extras-end-->`;

  const footer = clean.search(/<div\s+id=["']?footer/);
  if (footer >= 0) return clean.slice(0, footer) + block + clean.slice(footer);
  return clean.includes("</body>")
    ? clean.replace("</body>", block + "</body>")
    : clean + block;
};

/* ---------- Preview ---------- */

const lineItem = (SrNo, Name, SKU, Qty, RetailPrice, TaxableAmount, TaxAmount) => ({
  SrNo,
  Qty,
  RetailPrice,
  BasePrice: RetailPrice,
  SupplyPrice: RetailPrice,
  DiscountAmount: 0,
  DiscountPer: 0,
  SubTotal: TaxableAmount,
  TaxableAmount,
  TaxAmount,
  TaxPercentage: 18,
  TaxType: "GST",
  TaxGroup: "GST 18%",
  TotalTax: TaxAmount,
  HSNSACCode: "4820",
  UOM: "Nos",
  Amount: Qty * RetailPrice,
  Product: { Name, SKU, BarCode: SKU, MRP: RetailPrice, Description: "" },
  Employee: { EmployeeName: "" },
  TaxComponentAmount: [
    { TaxName: "CGST", TaxPercentage: 9, TaxAmount: TaxAmount / 2 },
    { TaxName: "SGST", TaxPercentage: 9, TaxAmount: TaxAmount / 2 }
  ]
});

const SAMPLE_ITEMS = [
  lineItem(1, "Sample Notebook", "SKU-001", 2, 120, 203.39, 36.61),
  lineItem(2, "Sample Pen Set", "SKU-002", 4, 45, 152.54, 27.46),
  lineItem(3, "Sample File Folder", "SKU-003", 1, 250, 211.86, 38.14)
];

// The store's real details with made-up line items
export const sampleData = (printConfig, store) => ({
  PrintConfig: printConfig,
  Sale: {
    TransactionNo: "SI/01/01/0001",
    TransactionDate: new Date().toLocaleDateString("en-GB"),
    Store: store,
    Customer: {
      CustomerCode: "C0001",
      FirstName: "Sample",
      LastName: "Customer",
      PhoneNo: "9000000000",
      EmailID: "customer@example.com"
    },
    BillingAddress: { Address1: "12 Sample Street", City: "Mumbai", State: "Maharashtra", PostalCode: "400001" },
    LineItems: SAMPLE_ITEMS,
    BillQty: 7,
    BillDiscount: 0,
    BillTaxAmount: 102.21,
    BillAmount: 670,
    BalanceAmount: 0,
    AmountInWords: "Rupees Six Hundred and Seventy Only",
    PrintTaxComponents: [
      { TaxName: "CGST", TaxAmount: 51.1 },
      { TaxName: "SGST", TaxAmount: 51.1 }
    ],
    TaxSummaryList: [
      {
        HSNSACCode: "4820",
        TotalQty: 7,
        TaxableAmount: 567.79,
        TaxComponentAmount: [
          { TaxName: "CGST", TaxPercentage: 9, TaxAmount: 51.1 },
          { TaxName: "SGST", TaxPercentage: 9, TaxAmount: 51.1 }
        ]
      }
    ],
    Payments: [{ PaymentMode: "Cash", Amount: 670 }]
  },
  Inventory: {
    TransactionNo: "PO/01/01/0001",
    TransactionDateShort: new Date().toLocaleDateString("en-GB"),
    Store: store,
    Vendor: { VendorFirstName: "Sample Vendor", Address1: "45 Market Road", City: "Pune", State: "Maharashtra", PhoneNo: "9000000001" },
    LineItems: SAMPLE_ITEMS,
    BillQty: 7,
    ApprovedBy: ""
  }
});

export const renderTemplate = (template, data) =>
  renderToStaticMarkup(<ReactMustache template={template} data={data} />);

/* ---------- Images ---------- */

// Stored inside the template, so keep them small
export const shrinkImage = (source, maxWidth, maxHeight) => {
  const width = source.naturalWidth || source.width;
  const height = source.naturalHeight || source.height;
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
};

// The page's Content-Security-Policy allows data: images but not blob:
export const readImageFile = (file, maxWidth, maxHeight) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const image = new Image();
      image.onload = () => resolve(shrinkImage(image, maxWidth, maxHeight));
      image.onerror = reject;
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

/* ---------- Saved templates ---------- */

// props.printConfigs comes from withStoreTemplates. The API returns a store's
// configs most recently edited first and every print path takes the first
// match, so index 0 is the template that prints by default.
export const getTemplates = (props, typeKey) =>
  props.printConfigs.filter(
    (c) => !c.IsDeleted && c.TransactionTypeID === typeKey
  );

export const getTransactionType = (props, typeKey) =>
  props.config.TransactionTypes.find((t) => t.key === typeKey);

export const getModule = (type) =>
  String((type && (type.Module || type.ModuleID)) || "").toLowerCase();

const moduleOf = (config) => String(config.ModuleID || config.Module).toLowerCase();

// The saved template whose show/hide settings suit a new one best: same paper
// size first, because a 3 inch setup hides columns an A4 page has room for
const pickBase = (configs, typeKey, module, size) => {
  const live = configs.filter((c) => !c.IsDeleted);
  const sameType = live.filter((c) => c.TransactionTypeID === typeKey);
  const sameModule = live.filter((c) => moduleOf(c) === module);
  return (
    sameType.find((c) => c.PrintSize === size) ||
    sameModule.find((c) => c.PrintSize === size) ||
    sameType[0] ||
    sameModule[0] ||
    live[0]
  );
};

// What a new template starts from. A store with no templates yet borrows the
// settings of the store signed into, but not its header and footer.
export const getStartingConfig = (props, typeKey, size) => {
  const type = getTransactionType(props, typeKey);
  const module = getModule(type);
  const base =
    pickBase(props.printConfigs, typeKey, module, size) ||
    pickBase(props.config.PrintConfigs, typeKey, module, size);

  return {
    ...(base ? toPayload(base) : DEFAULT_FIELDS),
    ...(base && base.StoreID === props.storeKey
      ? {}
      : { HeaderText: "", HeaderImage: null, FooterText: "", FooterImage: null }),
    RegistrationID: props.user.user.RegistrationID,
    StoreID: props.storeKey,
    ModuleID: module,
    Module: module.charAt(0).toUpperCase() + module.slice(1),
    TransactionTypeID: typeKey,
    TransactionType: type ? type.TransactionType : "",
    PrintSize: size,
    PrintHeight: 0,
    PrintOrientation: "",
    PreviousVersions: [],
    IsDeleted: false
  };
};

export const isSameModule = (config, module) => moduleOf(config) === module;

export const toPayload = ({ key, ActionOn, ...config }) => config;

// Any save stamps ActionOn, which would move that template into the default
// slot. Saving the default last keeps it in place.
export const keepDefault = (props, defaultTemplate) =>
  props.savePrintConfig(toPayload(defaultTemplate), defaultTemplate.key);
